import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import { SubmissionStateTag } from '@framework/frontoffice-submission/submission/state/SubmissionStateTag.tsx';
import { ISODate } from '@framework/ui/components/date/ISODate.tsx';
import { Space, Typography } from 'antd';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ReceiptField } from './receiptFields.ts';

const { Text } = Typography;

/**
 * Props comuni a tutti i componenti dei campi della ricevuta di invio.
 */
export interface IReceiptFieldProps {
  receiptSubmission: RichiestaDTO | undefined;
  submissionCodes: ReturnType<typeof SubmissionLogic.getCodes>;
}

/** Props dei campi che leggono solo la richiesta. */
type ISubmissionFieldProps = Readonly<
  Pick<IReceiptFieldProps, 'receiptSubmission'>
>;

/** Props dei campi che leggono solo i codici della richiesta. */
type ICodesFieldProps = Readonly<Pick<IReceiptFieldProps, 'submissionCodes'>>;

/**
 * Stato della richiesta.
 */
export function ReceiptStatusField({
  receiptSubmission,
}: ISubmissionFieldProps) {
  const { t } = useTranslation();
  return (
    <Space>
      <Text>{t('submission.stato-richiesta', 'Stato richiesta:')}</Text>
      <SubmissionStateTag submission={receiptSubmission} />
    </Space>
  );
}

/**
 * Numero del procedimento, mostrato solo se presente.
 */
export function ReceiptProcedureNumberField({
  submissionCodes,
}: ICodesFieldProps) {
  const { t } = useTranslation();
  if (!submissionCodes.procedureNumber) return null;

  return (
    <Space>
      <Text>{t('submission.numero-procedimento', 'Numero procedimento:')}</Text>
      <Text strong>{submissionCodes.procedureNumber}</Text>
    </Space>
  );
}

/**
 * Numero istanza (progressivo), mostrato solo se presente.
 */
export function ReceiptInstanceNumberField({
  receiptSubmission,
}: ISubmissionFieldProps) {
  const { t } = useTranslation();
  if (!receiptSubmission?.progressivo) return null;

  return (
    <Space>
      <Text>{t('submission.numero-istanza', 'Numero istanza:')}</Text>
      <Text strong>{receiptSubmission.progressivo}</Text>
    </Space>
  );
}

/**
 * Data di invio della richiesta.
 */
export function ReceiptSubmissionDateField({
  receiptSubmission,
}: ISubmissionFieldProps) {
  const { t } = useTranslation();
  return (
    <Space>
      <Text>{t('submission.data-invio', 'Data invio:')}</Text>
      <Text strong>
        <ISODate
          date={receiptSubmission?.dataInvio}
          format="DD/MM/YYYY HH:mm"
        />
      </Text>
    </Space>
  );
}

/**
 * Fase del procedimento, mostrata solo se presente.
 */
export function ReceiptProcedurePhaseField({
  receiptSubmission,
}: ISubmissionFieldProps) {
  const { t } = useTranslation();
  if (!receiptSubmission?.fase?.denominazione) return null;

  return (
    <Space>
      <Text>{t('submission.fase-procedimento', 'Fase del procedimento:')}</Text>
      <Text strong>{receiptSubmission.fase.denominazione}</Text>
    </Space>
  );
}

/**
 * Ufficio responsabile del procedimento, mostrato solo se presente.
 */
export function ReceiptProcedureManagerOfficeField({
  receiptSubmission,
}: ISubmissionFieldProps) {
  const { t } = useTranslation();
  if (!receiptSubmission?.ufficioResponsabileProcedimento) return null;

  return (
    <Space>
      <Text>
        {t(
          'submission.ufficio-responsabile-procedimento',
          'Ufficio responsabile:',
        )}
      </Text>
      <Text strong>{receiptSubmission.ufficioResponsabileProcedimento}</Text>
    </Space>
  );
}

/**
 * Responsabile del procedimento, mostrato solo se presente.
 */
export function ReceiptProcedureManagerField({
  receiptSubmission,
}: ISubmissionFieldProps) {
  const { t } = useTranslation();
  if (!receiptSubmission?.responsabileProcedimento) return null;

  return (
    <Space>
      <Text>
        {t(
          'submission.responsabile-procedimento',
          'Responsabile procedimento:',
        )}
      </Text>
      <Text strong>{receiptSubmission.responsabileProcedimento}</Text>
    </Space>
  );
}

/**
 * Mappa tra identificatore configurabile e componente che lo renderizza.
 * Il tipo `Record` sull'enum garantisce che ogni campo abbia il suo componente.
 */
export const receiptFieldComponents: Record<
  ReceiptField,
  React.ComponentType<IReceiptFieldProps>
> = {
  [ReceiptField.Status]: ReceiptStatusField,
  [ReceiptField.ProcedureNumber]: ReceiptProcedureNumberField,
  [ReceiptField.InstanceNumber]: ReceiptInstanceNumberField,
  [ReceiptField.SubmissionDate]: ReceiptSubmissionDateField,
  [ReceiptField.ProcedurePhase]: ReceiptProcedurePhaseField,
  [ReceiptField.ProcedureManagerOffice]: ReceiptProcedureManagerOfficeField,
  [ReceiptField.ProcedureManager]: ReceiptProcedureManagerField,
};
